import { Injectable } from '@angular/core';
import { LocalStorageService } from '../../../framework/localstorage.service';
import { environment } from '../../../environments/environment';
import { SwitchBackend } from '@fa-switch-ax/chatbot-backend';
import { ApplicationInsightsService } from './application-insights.service';
// Side-effect import: registers <fadv-chatbot> and all its sub-components as custom elements
import '@fa-switch-sharedservices/web-elements/src/lib/components/chatbot/chatbot';

// Import i18next via a .js ESM re-export helper to ensure we get the same singleton
// instance that the <fadv-chatbot> web component uses (see i18next-esm.js for details)
const { i18next, HttpBackend }: any = require('./i18next-esm');

// Load the web-elements light theme tokens (Lit CSSResult with .cssText property)
// These define CSS custom properties (--color-*, --spacing-*, --z-index-chatbot, etc.)
// that the chatbot's shadow DOM styles reference via var().
const elementsLightStyles: any = require('@fa-switch-sharedservices/web-elements/src/lib/styles/elements-light.styles').default;

declare var window: any;
declare var document: any;

/**
 * Maps application language codes (from localStorage 'language') to
 * i18next locale folder names used by web-elements assets.
 */
const LOCALE_MAP: { [key: string]: string } = {
  'bg': 'bg-BG',
  'cs': 'cs-CZ',
  'de': 'de-DE',
  'en': 'en-US',
  'en-gb': 'en-GB',
  'es': 'es-ES',
  'es-es': 'es-ES',
  'es-419': 'es-419',
  'es-mx': 'es-MX',
  'es-us': 'es-US',
  'fr': 'fr-FR',
  'fr-ca': 'fr-CA',
  'hu': 'hu-HU',
  'id': 'id-ID',
  'it': 'it-IT',
  'ja': 'ja-JP',
  'ko': 'ko-KR',
  'nl': 'nl-NL',
  'pl': 'pl-PL',
  'pt': 'pt-PT',
  'pt-br': 'pt-BR',
  'ro': 'ro-RO',
  'ru': 'ru-RU',
  'sk': 'sk-SK',
  'sv-se': 'sv-SE',
  'th': 'th-TH',
  'tr': 'tr-TR',
  'vi': 'vi-VN',
  'zh-cn': 'zh-CN',
  'zh-hk': 'zh-HK',
  'zh-tw': 'zh-TW',
  'zh-sg': 'zh-CN',
  'zh-my': 'zh-CN',
  'zu': 'zu-ZA',
};

@Injectable()
export class ChatService {

  private switchBackend: any = null;
  private _isAIChatbotInitialized = false;
  private _isI18nInitialized = false;

  private sanitizeUserInfo(rawUserInfo: any): any {
    const env: any = environment;
    const source = rawUserInfo || {};
    return {
      first_name: source.first_name,
      last_name: source.last_name,
      email: source.email,
      company_name: source.company_name,
      contact_type: source.contact_type || env.chatbotContactType || 'Candidate',
    };
  }

  constructor(
    private localStorage: LocalStorageService,
    private _appInsights: ApplicationInsightsService
  ) {}

  /**
   * Determines which chat experience to use.
   * Reads 'support_chat_experience' from localStorage;
   * falls back to environment default_chat_exp ('AI').
   */
  getChatExperience(): string {
    const env: any = environment;
    const preference = this.localStorage.getItem('support_chat_experience');
    return preference || env.default_chat_exp || 'AI';
  }

  isAIChatExperience(): boolean {
    return this.getChatExperience() === 'AI';
  }

  get isAIChatbotInitialized(): boolean {
    return this._isAIChatbotInitialized;
  }

  hasRequiredUserInfo(): boolean {
    return !!this.buildUserInfo();
  }

  /**
   * Builds chatbot userInfo from localStorage.
   * Returns null when required identity fields are missing so the pre-chat form can be shown.
   */
  private buildUserInfo(): any {
    const env: any = environment;
    const firstName = this.localStorage.getItem('first_name');
    const lastName = this.localStorage.getItem('last_name');
    const email = this.localStorage.getItem('applicant_email');
    const companyName = this.localStorage.getItem('company_name');

    if (!firstName || !lastName || !email || !companyName) {
      console.log('⚠️ Missing required fields, returning null for pre-chat form');
      return null;
    }

    const userInfo: any = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      company_name: companyName,
      contact_type: env.chatbotContactType || 'Candidate',
    };

    return userInfo;
  }

  /**
   * Sets userInfo on the chatbot element when data is available.
   */
  private setUserInfoOnChatbot(chatbotEl?: any): boolean {
    const element = chatbotEl || document.getElementById('ai-chatbot');
    if (!element) {
      return false;
    }

    const userInfo = this.buildUserInfo();
    if (!userInfo) {
      try {
        element.userInfo = null;
      } catch (error) {
        console.warn('Unable to clear chatbot userInfo during pre-chat fallback:', error);
      }
      element.preChatForm = true;
      return false;
    }

    const sanitizedUserInfo = this.sanitizeUserInfo(userInfo);

    element.userInfo = sanitizedUserInfo;
    // If the web component merges state internally, remove any non-allowed keys.
    if (element.userInfo && typeof element.userInfo === 'object') {
      const allowed = ['first_name', 'last_name', 'email', 'company_name', 'contact_type'];
      Object.keys(element.userInfo).forEach((key: string) => {
        if (allowed.indexOf(key) === -1) {
          delete element.userInfo[key];
        }
      });
    }

    // Valid userInfo should bypass pre-chat form.
    element.preChatForm = false;
    return true;
  }

  private mapLocale(language: string): string {
    if (!language) return 'en-US';
    const key = language.toLowerCase();
    if (LOCALE_MAP[key]) {
      return LOCALE_MAP[key];
    }
    // Try language-only part (e.g., 'fr-fr' → 'fr', 'de-de' → 'de')
    const langOnly = key.split('-')[0];
    return LOCALE_MAP[langOnly] || 'en-US';
  }

  /**
   * Initializes i18next with the components namespace so that
   * <fadv-chatbot> web component labels render correctly.
   * Must be called before initAIChatbot().
   */
  async initI18n(language: string): Promise<void> {
    if (this._isI18nInitialized) {
      return;
    }
    try {
      const lng = this.mapLocale(language);
      await i18next.use(HttpBackend).init({
        lng: lng,
        fallbackLng: 'en-US',
        load: 'currentOnly',
        ns: ['components'],
        defaultNS: 'components',
        returnEmptyString: false,
        backend: {
          loadPath: '/assets/locales/{{lng}}/{{ns}}.json',
        },
      });
      this._isI18nInitialized = true;
    } catch (error) {
      console.error('Failed to initialize i18next:', error);
      this._appInsights.trackException(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /**
   * Changes the i18next language at runtime (e.g., when user switches
   * language from the language-selection page).
   */
  async changeLanguage(language: string): Promise<void> {
    if (!this._isI18nInitialized) {
      await this.initI18n(language);
      return;
    }
    try {
      const lng = this.mapLocale(language);
      await i18next.changeLanguage(lng);
      this.setUserInfoOnChatbot();
      // Force chatbot to re-render with new translations.
      // Note: chatbot translations currently only exist in en-US within the
      // web-elements package. Other locales will fall back to English until
      // the web-elements team adds chatbot keys to all locale files.
      const chatbotEl = document.getElementById('ai-chatbot') as any;
      if (chatbotEl && typeof chatbotEl.requestUpdate === 'function') {
        chatbotEl.requestUpdate();
      }
    } catch (error) {
      console.error('Failed to change i18next language:', error);
      this._appInsights.trackException(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /**
   * Initializes the AI chatbot by creating the SwitchBackend
   * and connecting it to the <fadv-chatbot> web component in the DOM.
   */
  initAIChatbot(): void {
    if (this._isAIChatbotInitialized) {
      return;
    }

    // Inject web-elements theme tokens into document if not already present
    this.injectThemeStyles();

    try {
      this.switchBackend = new SwitchBackend(
        environment.chatbotApiKey,
        environment.chatbotApiUrl
      );

      // Connect backend to the web component after DOM renders
      setTimeout(() => {
        this.connectBackendToChatbot();
      }, 500);

      this._isAIChatbotInitialized = true;
    } catch (error) {
      console.error('Failed to initialize AI chatbot:', error);
      this._appInsights.trackException(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /**
   * Connects the SwitchBackend instance to the <fadv-chatbot> element.
   */
  private connectBackendToChatbot(): void {
    const chatbotEl = document.getElementById('ai-chatbot') as any;
    if (chatbotEl && this.switchBackend) {
      chatbotEl.backend = this.switchBackend;
      this.setUserInfoOnChatbot(chatbotEl);

      // The chatbot is a shadow DOM web component; patch mobile trigger width
      // inside its shadow root so the "Get Help" button doesn't stretch full-width.
      this.injectMobileTriggerWidthFix(chatbotEl);

      // Force Lit to re-render the component so that i18next t() calls
      // in the template pick up the now-loaded translations
      if (typeof chatbotEl.requestUpdate === 'function') {
        chatbotEl.requestUpdate();
      }

      // Listen for init events
      chatbotEl.addEventListener('fadv-chatbot-init-success', () => {
        this.injectMobileTriggerWidthFix(chatbotEl);
        console.log('AI chatbot initialized successfully');
      });
      chatbotEl.addEventListener('fadv-chatbot-init-failed', (event: any) => {
        console.error('AI chatbot initialization failed:', event?.detail);
        const detail = event?.detail;
        this._appInsights.trackException(detail instanceof Error ? detail : new Error('AI chatbot initialization failed: ' + JSON.stringify(detail)));
      });
    } else {
      console.warn('AI chatbot element not found in DOM');
    }
  }

  /**
   * Injects a small mobile-only override into the chatbot shadow root so
   * the trigger uses natural width instead of filling the full row.
   */
  private injectMobileTriggerWidthFix(chatbotEl: any): void {
    const styleId = 'fadv-chatbot-mobile-trigger-fix';
    const shadowRoot = chatbotEl && chatbotEl.shadowRoot;

    if (!shadowRoot || shadowRoot.getElementById(styleId)) {
      return;
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = '@media (max-width: 575px) {'
      + '.chatbot:not(.chatbot--open) {'
      + 'width: auto !important;'
      + 'pointer-events: none;'
      + '}'
      + '.chatbot:not(.chatbot--open) .chatbot__trigger {'
      + 'pointer-events: auto;'
      + '}'
      + '.chatbot.chatbot--bottom-right .chatbot__trigger,'
      + '.chatbot.chatbot--bottom-left .chatbot__trigger {'
      + 'width: fit-content !important;'
      + 'max-width: calc(100% - 1rem);'
      + 'margin-left: auto;'
      + '}'
      + '.chatbot.chatbot--bottom-right .chatbot__trigger .chatbot__trigger-button,'
      + '.chatbot.chatbot--bottom-left .chatbot__trigger .chatbot__trigger-button {'
      + 'width: auto !important;'
      + '}'
      + '}';

    shadowRoot.appendChild(style);
  }

  /**
   * Injects the web-elements light theme CSS custom properties into the document <head>.
   * The chatbot shadow DOM styles reference these via var(--color-*, --spacing-*, etc.).
   */
  private injectThemeStyles(): void {
    if (document.getElementById('fadv-elements-theme')) {
      return;
    }
    try {
      let cssText: string = elementsLightStyles.cssText;
      if (cssText) {
        // The full theme CSS contains global rules (html, body { background-color; },
        // *, ::after, ::before { box-sizing }) that override the app's existing styles.
        // Extract only CSS custom property declarations (--name: value;) so we inject
        // tokens without any element/selector rules that would affect the app layout.
        const tokenLines = cssText.split('\n')
          .filter((line: string) => line.trim().startsWith('--'))
          .join('\n');

        const style = document.createElement('style');
        style.id = 'fadv-elements-theme';
        // Scope tokens to :root so they inherit into shadow DOM.
        // Override Bootstrap 3's html { font-size: 10px } back to the browser
        // default (1rem = 16px) so the chatbot's rem-based sizing is correct.
        // Set explicit text color on <fadv-chatbot> to override body { color: #fff }.
        style.textContent = ':root {\n' + tokenLines + '\n}\n'
          + 'html { font-size: var(--font-size-medium, 1rem); }\n'
          + 'fadv-chatbot { color: var(--color-neutral-800, #363635); }';
        document.head.appendChild(style);
      }
    } catch (error) {
      console.error('Failed to inject web-elements theme styles:', error);
    }
  }

  /**
   * Cleans up AI chatbot resources.
   */
  shutdownAIChatbot(): void {
    // Clear backend reference on the DOM element
    const chatbotEl = document.getElementById('ai-chatbot') as any;
    if (chatbotEl) {
      chatbotEl.backend = null;
    }
    this.switchBackend = null;
    this._isAIChatbotInitialized = false;
  }

  isSalesforceChatExperience(): boolean {
    return this.getChatExperience() !== 'AI';
  }

  /**
   * Hides the Salesforce Embedded Messaging chat button from the DOM.
   */
  hideSalesforceChat(): void {
    try {
      const chatButton = document.querySelector('.embeddedMessagingConversationButton') as HTMLElement;
      if (chatButton) {
        chatButton.style.display = 'none';
      }
      // Also hide the entire embedded messaging frame if present
      const frame = document.querySelector('embeddedservice-bootstrap') as HTMLElement;
      if (frame) {
        frame.style.display = 'none';
      }
    } catch (error) {
      console.error('Failed to hide Salesforce chat:', error);
    }
  }

  /**
   * Shows the Salesforce Embedded Messaging chat button.
   */
  showSalesforceChat(): void {
    try {
      const chatButton = document.querySelector('.embeddedMessagingConversationButton') as HTMLElement;
      if (chatButton) {
        chatButton.style.display = '';
      }
      const frame = document.querySelector('embeddedservice-bootstrap') as HTMLElement;
      if (frame) {
        frame.style.display = '';
      }
    } catch (error) {
      console.error('Failed to show Salesforce chat:', error);
    }
  }
}
 